import { useState } from 'react';
import { Save, Shield, Lock, Unlock, Eye } from 'lucide-react';
import { cn } from 'utils/cn';
import { useUpdateUserPermissionsMutation } from 'redux/features/settings/settingsApi';
import { toast } from 'react-toastify';
import Switch from '../../../components/ui/Switch';
import Button from '../../../components/ui/Button';

const Permissions = ({ userId, userPermissions }) => {
  const [permissions, setPermissions] = useState({
    ipManagement: userPermissions?.ipManagement || 'none',
    departmentManagement: userPermissions?.departmentManagement || 'none',
  });

  const [updateUserPermissions, { isLoading }] = useUpdateUserPermissionsMutation();

  const handlePermissionChange = (module, value) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: value,
    }));
  };

  const toggleAccess = (module, isEnabled) => {
    handlePermissionChange(module, isEnabled ? 'read' : 'none');
  };

  const toggleWrite = (module, isWriteEnabled) => {
    handlePermissionChange(module, isWriteEnabled ? 'write' : 'read');
  };

  const handleSave = async () => {
    try {
      await updateUserPermissions({ id: userId, data: permissions }).unwrap();
      toast.success('Permissions updated successfully!');
    } catch {
      toast.error('Failed to update permissions.');
    }
  };

  const modules = [
    {
      id: 'ipManagement',
      name: 'IP Management',
      description: 'Manage IP addresses and network settings',
    },
    {
      id: 'departmentManagement',
      name: 'Department Management',
      description: 'Create, edit and manage departments',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}

        {/* Permissions List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">
                  User Permissions Management
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Configure access permissions for different modules
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {modules.map((module) => {
              const currentStatus = permissions[module.id];
              const hasAccess = currentStatus !== 'none';
              const hasWriteAccess = currentStatus === 'write';

              return (
                <div key={module.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{module.name}</h3>
                        {!hasAccess && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase rounded">
                            Disabled
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 max-w-md">{module.description}</p>
                    </div>

                    <div className="flex items-center gap-8 bg-gray-50 md:bg-transparent p-3 md:p-0 rounded-lg">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={hasAccess}
                            onChange={(checked) => toggleAccess(module.id, checked)}
                            size="sm"
                          />
                          <span className="text-xs font-medium text-gray-700">Access</span>
                        </div>
                        <span className="text-[10px] text-gray-400 ml-10">
                          Enable module visibility
                        </span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={hasWriteAccess}
                            disabled={!hasAccess}
                            onChange={(checked) => toggleWrite(module.id, checked)}
                            size="sm"
                          />
                          <span
                            className={cn(
                              'text-xs font-medium',
                              hasAccess ? 'text-gray-700' : 'text-gray-400',
                            )}
                          >
                            Write Access
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-400 ml-10">Allow editing data</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              loading={isLoading}
            >
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          </div>
        </div>

        {/* Current Permissions Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Live Status Summary</h3>
            <div className="h-px flex-1 bg-gray-100" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {modules.map((module) => {
              const status = permissions[module.id];
              return (
                <div
                  key={module.id}
                  className={cn(
                    'p-3 rounded-lg border flex flex-col gap-2',
                    status === 'write'
                      ? 'bg-green-50 border-green-100'
                      : status === 'read'
                        ? 'bg-blue-50 border-blue-100'
                        : 'bg-gray-50 border-gray-100',
                  )}
                >
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    {module.name}
                  </span>
                  <div className="flex items-center gap-2">
                    {status === 'write' ? (
                      <Unlock className="w-3 h-3 text-green-600" />
                    ) : status === 'read' ? (
                      <Eye className="w-3 h-3 text-blue-600" />
                    ) : (
                      <Lock className="w-3 h-3 text-gray-400" />
                    )}
                    <span
                      className={cn(
                        'text-xs font-semibold',
                        status === 'write'
                          ? 'text-green-700'
                          : status === 'read'
                            ? 'text-blue-700'
                            : 'text-gray-600',
                      )}
                    >
                      {status === 'write'
                        ? 'Read & Write'
                        : status === 'read'
                          ? 'Read Only'
                          : 'No Access'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permissions;
